import { $createParagraphNode } from "lexical";

/**
 * HTML import overrides shared by every Lexical editor in the app.
 *
 * `@lexical/code` claims <pre> (priority 0) and any multi-line <code>
 * (priority 1) during HTML import and turns them into a CodeNode, which renders
 * as <code class="editor-code" data-gutter> and is run through Prism. A CodeNode
 * with no language falls back to JavaScript, so ordinary prose that happens to
 * arrive wrapped in <pre> came out syntax-coloured - "this" as a keyword, "." as
 * punctuation.
 *
 * These conversions are registered through `initialConfig.html.import`, which
 * Lexical appends to the same per-tag list as the node conversions and resolves
 * by priority, so priority 4 outranks CodeNode's claim on both tags. Every
 * $generateNodesFromDOM call on an editor built with this config is covered -
 * paste, workboard switching, initial HTML seeding - without touching the call
 * sites.
 *
 * Only the import side changes: typing ``` still creates a real code block via
 * MarkdownShortcutPlugin, and CodeNode stays registered so existing documents
 * keep loading.
 */

const NEWLINE = /\r?\n/;

const isMultiLineCode = (element) =>
  NEWLINE.test(element.textContent || "") || element.querySelector("br") !== null;

/**
 * Rewrites "line one\nline two" text nodes as "line one" <br> "line two".
 *
 * A <pre> keeps its line breaks through white-space:pre alone; a paragraph does
 * not, so the newlines have to become real breaks before the subtree is handed
 * to Lexical or every line would collapse onto one. Safe to mutate: the DOM here
 * is a throwaway DOMParser document, and Lexical converts the children only
 * after this conversion function returns.
 */
const splitNewlinesIntoBreaks = (element) => {
  const doc = element.ownerDocument;
  if (!doc) return;

  // Collect first, then replace - swapping nodes mid-walk invalidates the walker.
  const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((textNode) => {
    const lines = (textNode.nodeValue || "").split(NEWLINE);
    if (lines.length < 2) return;

    const fragment = doc.createDocumentFragment();
    lines.forEach((line, index) => {
      if (index > 0) fragment.appendChild(doc.createElement("br"));
      if (line) fragment.appendChild(doc.createTextNode(line));
    });
    textNode.parentNode.replaceChild(fragment, textNode);
  });
};

/**
 * Theme class CodeNode puts on a code block (exampleTheme.code). Styled as the
 * grey code box in editor/css/styles.css.
 */
const CODE_BLOCK_CLASS = "editor-code";

/**
 * Tells a genuine code block apart from prose that merely arrived wrapped in a
 * <pre>. Three markers, between them covering every producer in the app:
 *
 *  - data-language / data-highlight-language: written by CodeNode's own
 *    exportDOM, so a block that has already been through the editor is still
 *    recognised on every later load;
 *  - class="editor-code": the theme class from that same export;
 *  - a <code> child: what marked() emits for a fenced block, which is the shape
 *    code arrives in from a chat answer or pasted markdown.
 *
 * Anything else - Lexical's <pre spellcheck="false"><span>...</span></pre> prose
 * export among it - is not code and becomes a paragraph.
 */
const isRealCodeBlock = (element) =>
  element.hasAttribute("data-language") ||
  element.hasAttribute("data-highlight-language") ||
  element.classList.contains(CODE_BLOCK_CLASS) ||
  element.querySelector(":scope > code") !== null;

/** Contributes no node of its own; children attach to the parent instead. */
const $unwrapElement = () => ({ node: null });

/** Imports a code block as a plain paragraph; its children convert as usual. */
const $convertCodeBlockToParagraph = (domNode) => {
  splitNewlinesIntoBreaks(domNode);

  // A markdown fence parses as <pre><code>...</code></pre>. The <pre> is
  // converted first and already supplied the paragraph, so the inner <code>
  // returns no node of its own and just hoists its children into it - returning
  // a second paragraph would nest one <p> inside another.
  const isInsideCodeBlock = domNode.parentElement?.closest("pre") != null;

  return { node: isInsideCodeBlock ? null : $createParagraphNode() };
};

/**
 * Drops list items that are already empty in the source HTML. A blank line typed
 * between bullets is exported by Lexical as <li><br></li>, and on the way back in
 * it imports as a real list item - a bare marker with no text beside it, on every
 * other row. Only the import is filtered: pressing Enter in a list still creates
 * an empty item to type into, exactly as before.
 *
 * An item holding media, a control or a nested list is never "empty", even when
 * it carries no text of its own. Mirrors removeEmptyListItems() in
 * src/components/ProcessRenderLatex.js - keep the two definitions in step.
 */
const isEmptyListItem = (element) =>
  element.querySelector(
    "img, svg, math, video, audio, iframe, input, a, ul, ol, table"
  ) === null &&
  (element.textContent || "").replace(/\u00a0/g, " ").trim() === "";

/** Drops the item entirely: no node of its own, and no children to hoist. */
const $dropListItem = (domNode) => {
  domNode.replaceChildren();
  return { node: null };
};

export const htmlImportConfig = {
  // Returning null stands aside for CodeNode, which builds the block and takes
  // its editor-code class straight from the theme - no marker has to be copied
  // onto the element by hand, and the class survives every later save/load.
  pre: (node) =>
    isRealCodeBlock(node)
      ? null
      : { conversion: $convertCodeBlockToParagraph, priority: 4 },

  li: (node) =>
    isEmptyListItem(node) ? { conversion: $dropListItem, priority: 4 } : null,

  code: (node) => {
    const parentPre = node.parentElement?.closest("pre");

    // <pre><code>: the <pre> is converted first and is already the code block,
    // so this wrapper contributes nothing. Without it CodeNode claims the <code>
    // too and nests a second code block inside the first.
    if (parentPre && isRealCodeBlock(parentPre)) {
      return { conversion: $unwrapElement, priority: 4 };
    }

    // A <code> carrying a code marker of its own stands on its own as a block.
    if (
      node.classList.contains(CODE_BLOCK_CLASS) ||
      /(^|s)language-/.test(node.className)
    ) {
      return null;
    }

    // Single-line <code> is inline formatting and is left to TextNode; only the
    // multi-line form is the one CodeNode would have turned into a block.
    return isMultiLineCode(node)
      ? { conversion: $convertCodeBlockToParagraph, priority: 4 }
      : null;
  },
};

export default htmlImportConfig;
